/**
 * The things other things stand on: a table, and a wedge.
 *
 * One shape, registered twice. Both are a solid standing on the floor with a
 * base, a side rising from each end of it, and a flat top — which is a
 * TRAPEZIUM, and a triangle is the case where the two sides meet before they
 * reach the top. So there is no second geometry for the wedge and no chance of
 * the two drifting apart: a table is this shape with upright sides, a wedge is
 * this shape with sloping ones, and the apex is where the top has run out.
 *
 * ```
 *      TL ─────────── TR          height of the top above the base
 *     /                \
 *    /  left     right  \         the angle each side makes with the base
 *   BL ───── base ────── BR       standing on the floor
 * ```
 *
 * ## Faces, and why a part has to say which one
 *
 * Three of the four sides can be stood on — the top and the two flanks — so
 * `Part.support` naming the surface is not enough and `Part.face` says which.
 * That is the whole reason the wedge is worth building: two blocks, one on
 * each slope, joined by a rope over a pulley bolted at the apex, is the
 * arrangement the chapter is about, and it needs two faces on one part.
 *
 * ## What a face contributes
 *
 * A block resting on one has TWO coordinates: along the face, and into it. The
 * second is held shut by a two-sided contact row, so nothing moves along it —
 * but its multiplier is the normal force, which is then solved for and checked
 * by `SF = ma` like every other force on the bench. Friction is a bounded row
 * against that multiplier, |f| <= uN, and static and kinetic fall out of
 * whether the body is moving rather than out of two rules.
 *
 * The alternative — one coordinate along the face, N reconstructed afterwards
 * from whatever the other forces left over — makes `checkNewton` circular (it
 * would be balancing an equation it had just been given the answer to) and
 * leaves friction coupled to a number that is not in the system. Hence the
 * second coordinate.
 *
 * ## What it does NOT model
 *
 * A block does not leave its face, and does not travel round a corner onto the
 * next one. `support` and `face` are stored relationships, so moving a block
 * from one slope to the other is an EDIT and never something that happens
 * mid-solve — see `types.ts`. When the numbers say a block would have lifted
 * off, the normal force comes out NEGATIVE and `diagnose` says so in words
 * rather than letting a wrong answer stand.
 *
 * A solid is fixed to the floor unless its base is let slip (`floorGrip`).
 * Then it is a body like a block, and every row a face writes is about the
 * block's motion RELATIVE to the face — so the face pushes the block and the
 * block pushes the face back, and a wedge on a smooth floor recoils.
 */
import { defOf, JOINS, numbered, PartKind, Support } from '../registry';
import { ACROSS, DOWN, dot } from '../vec';
import { extentAlong, isFree } from '../world';
import { Body, Constraint, Part, Port, RopeContact, Vec2, World } from '../types';

export interface SurfaceData {
    /** Metres along the floor. */
    base: number;
    /** Radians: the angle each side makes with the base. A right angle is an
     *  upright side, which is what makes this shape a table. */
    left: number;
    right: number;
    /** How far the top stands above the base. Where the sides would meet
     *  before reaching it, they meet — and the top is a point. */
    height: number;
    /** How rough each face is — absent is smooth, the frictionless plane every
     *  book starts on. One per face, because a wedge's two slopes are two
     *  questions; a face that is not there (a wedge's top) has no say. */
    muTop?: number;
    muLeft?: number;
    muRight?: number;
    /** Join points the reader has made along its top, each kept as an offset
     *  from `at` so that moving the surface carries them with it. Where each
     *  one actually is — which face, how high — is always read off the shape,
     *  so resizing the surface cannot leave a point floating off it. */
    mounts?: { id: string; dx: number }[];
    /** How its base grips the floor — see `PartKind.floorGrip`. Absent means
     *  FIXED, unlike a block: a table or a wedge is structure unless the reader
     *  lets it go, and every ramp in a book stays put. */
    floorGrip?: number | 'fixed';
    /** kg, when the reader has set one. Absent means worked out from its size
     *  — see `SURFACE_DENSITY`. Only a sliding surface has a mass at all. */
    mass?: number;
    [k: string]: unknown;
}

const unit = (x: number, y: number): Vec2 => {
    const L = Math.hypot(x, y) || 1;
    return { x: x / L, y: y / L };
};

/** Where the two sides would meet: the tallest a top can be and still exist. */
export const apexHeight = (base: number, left: number, right: number): number => {
    const tl = Math.tan(left), tr = Math.tan(right);
    return (base * tl * tr) / (tl + tr);
};

/**
 * Its four corners, in bench coordinates. `at` is the middle of the BASE.
 *
 * Measured from the base because the base is what stands on the floor, so a
 * surface put down at floor level stays at floor level whatever its height and
 * whatever its angles. Measuring from the middle of the solid would move it
 * every time an angle changed.
 */
export function corners(part: Part<SurfaceData>): { bl: Vec2; br: Vec2; tl: Vec2; tr: Vec2; height: number } {
    const { base, left, right } = part.data;
    // The top cannot be narrower than nothing: past the apex the two sides have
    // already met, and the shape is a triangle however tall it was asked to be.
    const apex = apexHeight(base, left, right);
    const height = Math.min(part.data.height, apex);
    const bl = { x: part.at.x - base / 2, y: part.at.y };
    const br = { x: part.at.x + base / 2, y: part.at.y };
    return {
        bl, br, height,
        tl: { x: bl.x + height / Math.tan(left), y: part.at.y + height },
        tr: { x: br.x - height / Math.tan(right), y: part.at.y + height },
    };
}

/**
 * The point on its TOP straight above or below x — the highest face there,
 * never an upright side, and clamped to the nearest end past either edge.
 *
 * It is what "press anywhere on the wedge" means: a reader aims at a place
 * along the shape, not at a spot inside the wood, so the point is lifted to
 * the surface a pulley could actually be fastened to.
 */
export function topAt(part: Part<SurfaceData>, x: number): Vec2 {
    let best: Vec2 | undefined;
    let nearest: { at: Vec2; miss: number } | undefined;
    for (const f of facesOf(part)) {
        if (Math.abs(f.along.x) < 1e-6) continue;
        const want = (x - f.mid.x) / f.along.x;
        const s = Math.max(-f.half, Math.min(f.half, want));
        const at = { x: f.mid.x + f.along.x * s, y: f.mid.y + f.along.y * s };
        const miss = Math.abs(want - s) * Math.abs(f.along.x);
        if (miss < 1e-9) { if (!best || at.y > best.y) best = at; }
        else if (!nearest || miss < nearest.miss) nearest = { at, miss };
    }
    return best ?? nearest?.at ?? { ...part.at };
}

/**
 * How heavy a surface is per square metre of its drawn outline, when the
 * reader has not said: 5 kg/m². Chosen so a wedge is the same order as the
 * blocks on it — the default 4 m, 30° wedge comes out near 11.5 kg — which is
 * the textbook's recoiling-wedge problem, not a mountain that cannot move.
 */
export const SURFACE_DENSITY = 5;

/** Its mass from its size: the area of its outline at `SURFACE_DENSITY`. */
export function surfaceMass(part: Part<SurfaceData>): number {
    const pts = solidOutline(part);
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const p = pts[i], q = pts[(i + 1) % pts.length];
        a += p.x * q.y - q.x * p.y;
    }
    return (Math.abs(a) / 2) * SURFACE_DENSITY;
}

/** How rough one face is. */
export const faceMu = (part: Part<SurfaceData>, face: string): number =>
    ({ top: part.data.muTop, left: part.data.muLeft, right: part.data.muRight } as Record<string, number | undefined>)[face] ?? 0;

/** Can its base move at all? Anything short of fixed — even a grip that holds
 *  today may let go under a heavier block. */
export const slips = (part: Part<SurfaceData>): boolean => (part.data.floorGrip ?? 'fixed') !== 'fixed';

/** Has the top run out to a point? */
const pointed = (c: { tl: Vec2; tr: Vec2 }): boolean =>
    Math.hypot(c.tr.x - c.tl.x, c.tr.y - c.tl.y) < 1e-6;

/** Its silhouette: a triangle once the sides have met, a trapezium before. */
function solidOutline(part: Part<SurfaceData>): Vec2[] {
    const c = corners(part);
    return pointed(c) ? [c.bl, c.tl, c.br] : [c.bl, c.tl, c.tr, c.br];
}

/** One straight stretch something can stand on. */
interface Face {
    id: string;
    label: string;
    from: Vec2;
    to: Vec2;
    mid: Vec2;
    /** Along the face, and out of it — the side things rest on. */
    along: Vec2;
    out: Vec2;
    half: number;
}

function makeFace(id: string, label: string, from: Vec2, to: Vec2, outward: Vec2): Face {
    return {
        id, label, from, to,
        mid: { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
        along: unit(to.x - from.x, to.y - from.y),
        out: unit(outward.x, outward.y),
        half: Math.hypot(to.x - from.x, to.y - from.y) / 2,
    };
}

/**
 * The faces something can stand on: the top, and the two flanks.
 *
 * Not the base — it is on the floor, and there is nothing under it. Each runs
 * DOWNHILL, which is what makes "it slid to the far end" mean the same thing
 * on a slope as on a table. A top that has run out to a point is left out
 * rather than offered as a face of no length.
 */
function facesOf(part: Part<SurfaceData>): Face[] {
    const c = corners(part);
    const out: Face[] = [];
    if (!pointed(c)) out.push(makeFace('top', 'the top', c.tl, c.tr, { x: 0, y: 1 }));
    out.push(makeFace('left', 'the left face', c.tl, c.bl,
        { x: -Math.sin(part.data.left), y: Math.cos(part.data.left) }));
    out.push(makeFace('right', 'the right face', c.tr, c.br,
        { x: Math.sin(part.data.right), y: Math.cos(part.data.right) }));
    return out;
}

const supportOn = (f: Face): Support => ({
    axes: [f.along, { x: -f.out.x, y: -f.out.y }],
    half: f.half,
    seat: (s, clear) => ({
        x: f.mid.x + f.along.x * s + f.out.x * clear,
        y: f.mid.y + f.along.y * s + f.out.y * clear,
    }),
    along: (at) => (at.x - f.mid.x) * f.along.x + (at.y - f.mid.y) * f.along.y,
});

/** Every row one face contributes, for everything standing on it. */
function rowsFor(f: Face, surface: Part, mu: number, w: World, bodies: Body[]): Constraint[] {
    const out: Constraint[] = [];
    w.parts
        .filter(p => p.support === surface.id && (p.face ?? f.id) === f.id)
        .forEach(p => {
            const body = bodies.find(b => b.partIds.includes(p.id));
            if (!body || !body.axes.length) return;
            /* Which coordinate is which is read back off the body rather than
             * assumed: a supported part welded to something else is still one
             * body, and the order its axes came out in is not this file's to
             * guarantee. */
            const into = body.axes.findIndex(a => dot(a, f.out) < -0.999);
            const down = body.axes.findIndex(a => Math.abs(dot(a, f.along)) > 0.999);
            if (into < 0 || down < 0) return;
            const sense = Math.sign(dot(body.axes[down], f.along));
            const rest = `rest:${p.id}`;
            /* A surface that SLIDES moves the face out from under the block, so
             * every row here is about the block's motion RELATIVE to the face:
             * each gets a term on each of the surface's coordinates — how much
             * of that coordinate's travel lies along the direction the row
             * measures, with the sign turned. The rows' multipliers then act on
             * both — the face pushes the block, the block pushes the face back
             * — which is what makes a wedge on a smooth floor recoil. */
            const surfBody = bodies.find(b => b.partIds.includes(surface.id));
            const moving = surfBody && surfBody !== body && isFree(surfBody) ? surfBody : undefined;
            const relative = (rows: Constraint['rows']): Constraint['rows'] => (!moving ? rows : [
                ...rows,
                ...moving.axes.map((axis, j) => ({
                    coord: { bodyId: moving.id, axis: j },
                    coefficient: -rows.reduce((s, r) => s + r.coefficient * dot(body.axes[r.coord.axis], axis), 0),
                })).filter(r => Math.abs(r.coefficient) > 1e-12),
            ]);

            /* The face itself. TWO-sided, and the only two-sided row on the
             * bench: a face pushes a block out and — because what a part rests
             * on is stored — holds it in too. A negative N is then the bench
             * telling the reader that this block would have lifted off. */
            out.push({
                id: rest,
                kind: 'contact',
                rows: relative([{ coord: { bodyId: body.id, axis: into }, coefficient: -1 }]),
                gap: 0,
                oneSided: false,
                reports: 'normal',
                sourceId: p.id,
                label: f.label,
            });

            if (mu > 0) {
                out.push({
                    id: `grip:${p.id}`,
                    kind: 'friction',
                    rows: relative([{ coord: { bodyId: body.id, axis: down }, coefficient: 1 }]),
                    gap: 0,
                    oneSided: false,
                    coupledTo: { constraintId: rest, factor: mu },
                    reports: 'friction',
                    sourceId: p.id,
                    label: f.label,
                });
            }

            /* The face runs out. Without this a block slides off a table and
             * keeps going through the air, which the drawing shows and the
             * numbers do not contradict. */
            const clear = extentAlong(w, p, f.along);
            const s0 = (p.at.x - f.mid.x) * f.along.x + (p.at.y - f.mid.y) * f.along.y;
            ([[+1, f.half - clear - s0], [-1, f.half - clear + s0]] as [number, number][])
                .forEach(([way, gap], i) => {
                    out.push({
                        id: `edge:${p.id}:${i}`,
                        kind: 'contact',
                        rows: relative([{ coord: { bodyId: body.id, axis: down }, coefficient: -way * sense }]),
                        gap,
                        oneSided: true,
                        reports: 'normal',
                        sourceId: p.id,
                        label: `the end of ${f.label}`,
                    });
                });
        });
    return out;
}

const MIN_BASE = 0.6, MAX_BASE = 5, MIN_HEIGHT = 0.2;
/** The same limits the sliders keep: 10° to upright. */
const sideAngle = (rise: number, run: number): number =>
    Math.max((10 * Math.PI) / 180, Math.min(Math.PI / 2, Math.atan2(rise, Math.max(0, run))));

/**
 * One corner of a table or a wedge dragged to `to`.
 *
 * A FOOT slides along the floor: the other foot and the top stay where they
 * are, so the base and that side's slope change, and the middle of the base
 * moves. A TOP corner sets the height of the whole top — a table's top stays
 * level — and its own side's slope, while the other top corner keeps its x. An
 * APEX sets the height and both slopes over the same base.
 */
function resizeSolid(part: Part<SurfaceData>, handle: string, to: Vec2, pointy: boolean): { data: Partial<SurfaceData>; at?: Vec2 } {
    const c = corners(part);
    const floor = part.at.y;
    const heightFor = (base: number, left: number, right: number, wanted: number): number => (pointy
        ? apexHeight(base, left, right)
        : Math.max(MIN_HEIGHT, Math.min(wanted, apexHeight(base, left, right) - 0.05)));

    if (handle === 'bl' || handle === 'br') {
        const isLeft = handle === 'bl';
        const stays = isLeft ? c.br.x : c.bl.x;
        const base = Math.max(MIN_BASE, Math.min(MAX_BASE, Math.abs(stays - to.x)));
        const foot = isLeft ? stays - base : stays + base;
        const side = isLeft ? sideAngle(c.height, c.tl.x - foot) : sideAngle(c.height, foot - c.tr.x);
        const left = isLeft ? side : part.data.left, right = isLeft ? part.data.right : side;
        return {
            at: { x: (foot + stays) / 2, y: floor },
            data: { base, left, right, height: heightFor(base, left, right, part.data.height) },
        };
    }

    const rise = Math.max(MIN_HEIGHT, to.y - floor);
    if (handle === 'apex') {
        const left = sideAngle(rise, to.x - c.bl.x), right = sideAngle(rise, c.br.x - to.x);
        return { data: { left, right, height: heightFor(part.data.base, left, right, rise) } };
    }
    const isLeft = handle === 'tl';
    const left = isLeft ? sideAngle(rise, to.x - c.bl.x) : sideAngle(rise, c.tl.x - c.bl.x);
    const right = isLeft ? sideAngle(rise, c.br.x - c.tr.x) : sideAngle(rise, c.br.x - to.x);
    return { data: { left, right, height: heightFor(part.data.base, left, right, rise) } };
}

/**
 * One kind of solid, twice: upright sides make a table, sloping ones a wedge.
 *
 * They differ in the shape they are created at and in what they are called, so
 * that is all the two entries below differ by. Roughness is a number on either
 * and not a third part, for the same reason.
 */
function solid(key: string, label: string, tag: string, start: () => SurfaceData, derived?: string[]): PartKind<SurfaceData> {
    return {
        key,
        label,
        tag,
        ...(derived ? { derived } : {}),
        home: 0,
        /** It stands on the floor and stays there. */
        onFloor: true,
        create: (at, id): Part<SurfaceData> => ({ id, kind: key, at, data: start() }),

        /** Its corners — both feet, and the two top corners or the apex. */
        handles: (part) => {
            const c = corners(part);
            return pointed(c)
                ? [{ id: 'bl', at: c.bl }, { id: 'apex', at: c.tl }, { id: 'br', at: c.br }]
                : [{ id: 'bl', at: c.bl }, { id: 'tl', at: c.tl }, { id: 'tr', at: c.tr }, { id: 'br', at: c.br }];
        },
        resize: (part, handle, to) => resizeSolid(part, handle, to, !!derived?.includes('height')),

        /** Both feet and both top corners — or the apex, when the top has run
         *  out. The corner is what matters: a pulley bolted at the apex of a
         *  wedge, with a block on each slope, is the arrangement this part
         *  exists for. */
        ports: (part): Port[] => {
            const c = corners(part);
            const foot = (id: string, at: Vec2): Port => ({ id, partId: part.id, role: 'anchorage', joins: JOINS.any, at });
            // A point pressed along the top exists for the one join that made it.
            const mounts = (part.data.mounts ?? []).map(m => ({ ...foot(m.id, topAt(part, part.at.x + m.dx)), joins: JOINS.one }));
            return pointed(c)
                ? [foot('footL', c.bl), foot('apex', c.tl), foot('footR', c.br), ...mounts]
                : [foot('footL', c.bl), foot('endL', c.tl), foot('endR', c.tr), foot('footR', c.br), ...mounts];
        },
        mountAt: (part, x) => {
            const mounts = part.data.mounts ?? [];
            const dx = x - part.at.x;
            // A second press in the same place is the same point, not two.
            const near = mounts.find(m => Math.abs(m.dx - dx) < 0.01);
            if (near) return { part, portId: near.id };
            let n = 1;
            while (mounts.some(m => m.id === `mount${n}`)) n++;
            const id = `mount${n}`;
            return { part: { ...part, data: { ...part.data, mounts: [...mounts, { id, dx }] } }, portId: id };
        },
        tidy: (part, used) => {
            const mounts = part.data.mounts ?? [];
            const kept = mounts.filter(m => used.has(m.id));
            return kept.length === mounts.length ? part : { ...part, data: { ...part.data, mounts: kept } };
        },

        /* Fixed, it is structure and has no mass the machine could feel.
           Sliding, it has one: the reader's, or its size at SURFACE_DENSITY. */
        mass: (part) => (slips(part) ? (part.data.mass ?? surfaceMass(part)) : 0),
        floorGrip: (part) => part.data.floorGrip ?? 'fixed',
        /** Measured from `at`, the middle of the base — so this says how far it
         *  reaches sideways and how tall it stands, not a box centred on
         *  anything. Only the click target and `reach` ask. */
        extent: (part): Vec2 => ({ x: part.data.base / 2, y: corners(part).height }),
        outline: (part): Vec2[] => solidOutline(part),

        /* Both ways, as a block has, once its base can slip: along the floor,
           and up off it. The second is what the floor's push is measured on,
           and a rough base's friction is a fraction of that push. */
        freedom: (part) => (slips(part) ? { axes: [ACROSS, DOWN] } : { axes: [] }),
        bondable: true,
        keyFacts: (part) => [`${+part.data.base.toFixed(2)} × ${+part.data.height.toFixed(2)} m`],
        ropeContact: (part, port): RopeContact => ({ at: port.at }),

        faces: (part) => facesOf(part).map(f => ({ id: f.id, label: f.label })),
        supports: (part, _w, which) => {
            const fs = facesOf(part);
            return supportOn(fs.find(f => f.id === which) ?? fs[0]);
        },
        constraints: (part, w, bodies) =>
            facesOf(part).flatMap(f => rowsFor(f, part, faceMu(part, f.id), w, bodies)),

        /** In its MIDDLE, on the wood. Out at its left corner the name sat on
         *  the floor beside the shape, closer to whatever stood there than to
         *  the table it named — and nothing stands inside a solid, so its
         *  middle is the one place a label can never be in the way. */
        nameAt: (part) => {
            const box = solidOutline(part);
            const mid = box.reduce((a, v) => ({ x: a.x + v.x / box.length, y: a.y + v.y / box.length }), { x: 0, y: 0 });
            return { at: mid, anchor: 'middle' as const };
        },
        name: (part, w) => numbered(part, w, tag),
    };
}

/** Upright sides and a flat top 1.2 m up: a bench to stand things on. */
export const table = solid('table', 'Table', 'T', () =>
    ({ base: 2.4, left: Math.PI / 2, right: Math.PI / 2, height: 1.2 }));

/** Both slopes at thirty degrees, and tall enough that they meet — because
 *  `g sin 30` is `g / 2`, and a reader can check the bench against their own
 *  arithmetic without reaching for a calculator. */
export const incline = solid('incline', 'Wedge', 'S', () =>
    ({ base: 3, left: Math.PI / 6, right: Math.PI / 6, height: apexHeight(3, Math.PI / 6, Math.PI / 6) }),
    /* A wedge's height is where its sides MEET. Change the base or an angle
     * and it follows; there is nothing to set, so nothing is offered. */
    ['height']);
